import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/api';

export function useReviewData(jobId) {
  const [job, setJob] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetDetails, setSheetDetails] = useState(null);
  const [sheetImage, setSheetImage] = useState(null);
  const [imageVersion, setImageVersion] = useState('original');
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const imgReq = useRef(0);
  const detailReq = useRef(0);

  const fetchJob = useCallback(async () => {
    try {
      const res = await api.get(`/drawing-jobs/${jobId}`);
      setJob(res.data);
      setSheets(res.data.sheets || []);
      if (res.data.sheets?.length > 0 && !selectedSheet) {
        setSelectedSheet(res.data.sheets[0]);
      }
    } catch (err) {
      console.error('Failed to load job:', err);
    } finally {
      setLoading(false);
    }
  }, [jobId, selectedSheet]);

  useEffect(() => { fetchJob(); }, [fetchJob]);

  const fetchSheetImage = useCallback(async () => {
    if (!selectedSheet) return;
    const myReq = ++imgReq.current;
    setImageLoading(true);
    try {
      const res = await api.get(`/drawing-jobs/${jobId}/sheets/${selectedSheet.id}/image?version=${imageVersion}`);
      if (myReq === imgReq.current) setSheetImage(res.data);
    } catch (err) {
      console.error('Failed to load sheet image:', err);
      if (myReq === imgReq.current) setSheetImage(null);
    } finally {
      if (myReq === imgReq.current) setImageLoading(false);
    }
  }, [jobId, selectedSheet, imageVersion]);

  useEffect(() => { fetchSheetImage(); }, [fetchSheetImage]);

  const fetchSheetDetails = useCallback(async () => {
    if (!selectedSheet) return;
    const myReq = ++detailReq.current;
    try {
      const res = await api.get(`/drawing-jobs/${jobId}/sheets/${selectedSheet.id}/details`);
      if (myReq === detailReq.current) setSheetDetails(res.data);
    } catch (err) {
      console.error('Failed to load sheet details:', err);
    }
  }, [jobId, selectedSheet]);

  useEffect(() => { fetchSheetDetails(); }, [fetchSheetDetails]);

  return {
    job, sheets, selectedSheet, setSelectedSheet,
    sheetDetails, sheetImage, imageVersion, setImageVersion,
    loading, imageLoading,
    fetchJob, fetchSheetDetails, fetchSheetImage,
  };
}
